import { Button, toast, Typography } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { APIError } from "api/utils"
import PageContainer from "components/PageContainer"
import { InfoCircle } from "iconoir-react"
import { useState } from "react"

export type Check = "ai" | "internal"

const RERUN = {
  ai: api.submissions.rerunAiDetection,
  internal: api.submissions.rerunPlagiarism,
}

const LABELS: Record<Check, { retry: string; running: string }> = {
  ai: {
    retry: "Retry AI detection",
    running: "AI detection is running. This can take a few minutes.",
  },
  internal: {
    retry: "Retry internal plagiarism",
    running:
      "Internal plagiarism check is running. This can take a few minutes.",
  },
}

type ButtonProps = {
  check: Check
  submissionId: string
  hadesMissing: boolean
  onQueued: (checks: Check[]) => void
}

const RetryButton = ({
  check,
  submissionId,
  hadesMissing,
  onQueued,
}: ButtonProps) => {
  // 404 means the submission is in neither hades nor Orbit: retrying won't help
  const [notFound, setNotFound] = useState(false)

  const { mutate, isPending } = useMutation({
    mutationFn: () => RERUN[check]({ urlParams: { submissionId } }),
    // When hades didn't have the submission, either button imports it and starts both checks
    onSuccess: () => onQueued(hadesMissing ? ["ai", "internal"] : [check]),
    onError: e => {
      const status = e instanceof APIError ? e.statusCode : undefined
      // Already running or done: hide the button and let polling reload the results
      if (status === 409) return onQueued([check])
      if (status === 404) setNotFound(true)
      toast.error(
        (e instanceof APIError && e.errors.message) ||
          (status === 404
            ? "This submission couldn't be found for plagiarism checks."
            : "Something went wrong. Please try again later.")
      )
    },
  })

  return (
    <Button
      size="sm"
      variant="outlined"
      loading={isPending}
      disabled={isPending || notFound}
      onClick={() => mutate()}
    >
      {LABELS[check].retry}
    </Button>
  )
}

type BannerProps = {
  check: Check
  submissionId: string
  hadesMissing: boolean
  /** The finished result: shown instead of a button */
  result?: { tone: string; message: string; value?: string }
  showRetry: boolean
  running: boolean
  onQueued: (checks: Check[]) => void
}

const PENDING_MESSAGE: Record<Check, string> = {
  ai: "AI detection hasn't been completed for this submission.",
  internal:
    "The internal plagiarism check hasn't been completed for this submission.",
}

/** One banner per check, always shown: the result once calculated, otherwise its status and retry button */
export const CheckBanner = ({
  check,
  result,
  showRetry,
  running,
  ...buttonProps
}: BannerProps) => (
  <div className={result?.tone ?? "bg-onSurface-50 text-onSurface-800"}>
    <PageContainer>
      <div className="flex flex-col items-center justify-between gap-0.5 py-1 md:flex-row md:py-1.5">
        <Typography className="flex items-center gap-1 text-sm">
          <span>
            <InfoCircle className="size-2.25" />
          </span>
          {result?.message ??
            (running ? LABELS[check].running : PENDING_MESSAGE[check])}
        </Typography>
        {result
          ? result.value && (
              <Typography variant="strong">{result.value}</Typography>
            )
          : showRetry &&
            !running && <RetryButton check={check} {...buttonProps} />}
      </div>
    </PageContainer>
  </div>
)
